import { FIELD, type MatchState } from "@golazo/shared";
import { JUICE } from "./config.js";

export interface AmbientAudioContext {
  ballSpeed: number;
  ballX: number;
  ballLoft: number;
  phase: MatchState["phase"];
}

export interface AmbientAudio {
  /** Arranca el murmullo continuo (tras desbloquear audio). */
  start(): void;
  update(dt: number, ctx: AmbientAudioContext): void;
  onGoal(): void;
  /** Parada, palo o tiro cerquita: "ooooh". */
  onGasp(intensity?: number): void;
  /** Remate o contra rapida hacia porteria. */
  onAttackSwell(intensity?: number): void;
  dispose(): void;
}

type AudioEnsure = () => AudioContext | null;
type MasterGet = () => GainNode | null;

/** Ruido rosa suave para capas de publico. */
function fillPinkNoise(data: Float32Array): void {
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;

  for (let i = 0; i < data.length; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.969 * b2 + white * 0.153852;
    b3 = 0.8665 * b3 + white * 0.3104856;
    b4 = 0.55 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.016898;
    const pink = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
    b6 = white * 0.115926;
    data[i] = pink * 0.11;
  }
}

function makeNoiseBuffer(context: AudioContext, seconds: number): AudioBuffer {
  const length = Math.floor(context.sampleRate * seconds);
  const buffer = context.createBuffer(2, length, context.sampleRate);
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    fillPinkNoise(buffer.getChannelData(ch));
  }
  return buffer;
}

/**
 * Ambiente de estadio sintetizado: murmullo continuo, brisas de grada,
 * reacciones a goles y momentos de peligro. Sin assets externos.
 */
export function createAmbientAudio(ensure: AudioEnsure, getMaster: MasterGet): AmbientAudio {
  const cfg = JUICE.AMBIENT;

  let started = false;
  let running = false;

  let bedGain: GainNode | null = null;
  let shimmerGain: GainNode | null = null;
  let reactGain: GainNode | null = null;

  let targetBed = cfg.BASE_LEVEL;
  let currentBed = cfg.BASE_LEVEL;
  let cheerBoost = 0;
  let swellBoost = 0;
  let gaspBoost = 0;
  let chatterTimer = 4 + Math.random() * 8;
  let grassCooldown = 0;
  let swellCooldown = 0;

  /** Nodos en bucle que hay que parar explicitamente al salir del partido. */
  const loopNodes: AudioBufferSourceNode[] = [];
  const oscNodes: OscillatorNode[] = [];

  const trackLoop = (source: AudioBufferSourceNode): void => {
    loopNodes.push(source);
  };

  const trackOsc = (osc: OscillatorNode): void => {
    oscNodes.push(osc);
  };

  const stopAll = (): void => {
    for (const source of loopNodes) {
      try {
        source.stop();
      } catch {
        /* ya parado */
      }
      source.disconnect();
    }
    loopNodes.length = 0;
    for (const osc of oscNodes) {
      try {
        osc.stop();
      } catch {
        /* ya parado */
      }
      osc.disconnect();
    }
    oscNodes.length = 0;
    bedGain = null;
    shimmerGain = null;
    reactGain = null;
  };

  const connectBus = (node: AudioNode, level: number): GainNode | null => {
    const master = getMaster();
    const context = ensure();
    if (!context || !master) return null;

    const bus = context.createGain();
    bus.gain.value = level;
    node.connect(bus);
    bus.connect(master);
    return bus;
  };

  const startLoops = (): void => {
    const context = ensure();
    const master = getMaster();
    if (!context || !master || running) return;
    running = true;

    const noise = makeNoiseBuffer(context, 4.2);

    // Murmullo principal de grada (medios-bajos).
    const bedSource = context.createBufferSource();
    bedSource.buffer = noise;
    bedSource.loop = true;

    const bedFilter = context.createBiquadFilter();
    bedFilter.type = "bandpass";
    bedFilter.frequency.value = 520;
    bedFilter.Q.value = 0.55;

    const bedLfo = context.createOscillator();
    bedLfo.type = "sine";
    bedLfo.frequency.value = 0.11;
    const bedLfoDepth = context.createGain();
    bedLfoDepth.gain.value = 90;
    bedLfo.connect(bedLfoDepth);
    bedLfoDepth.connect(bedFilter.frequency);
    bedLfo.start();
    trackOsc(bedLfo);

    bedSource.connect(bedFilter);
    bedGain = connectBus(bedFilter, cfg.BASE_LEVEL);
    bedSource.start();
    trackLoop(bedSource);

    // Capa "voces" mas aguda, muy sutil.
    const shimmerSource = context.createBufferSource();
    shimmerSource.buffer = noise;
    shimmerSource.loop = true;
    shimmerSource.playbackRate.value = 1.03;

    const shimmerFilter = context.createBiquadFilter();
    shimmerFilter.type = "bandpass";
    shimmerFilter.frequency.value = 1380;
    shimmerFilter.Q.value = 0.45;

    const shimmerLfo = context.createOscillator();
    shimmerLfo.type = "triangle";
    shimmerLfo.frequency.value = 0.07;
    const shimmerLfoDepth = context.createGain();
    shimmerLfoDepth.gain.value = 220;
    shimmerLfo.connect(shimmerLfoDepth);
    shimmerLfoDepth.connect(shimmerFilter.frequency);
    shimmerLfo.start();
    trackOsc(shimmerLfo);

    shimmerSource.connect(shimmerFilter);
    shimmerGain = connectBus(shimmerFilter, cfg.SHIMMER_LEVEL);
    shimmerSource.start();
    trackLoop(shimmerSource);

    // Aire del estadio (viento leve / eco lejano).
    const airSource = context.createBufferSource();
    airSource.buffer = noise;
    airSource.loop = true;
    airSource.playbackRate.value = 0.97;

    const airFilter = context.createBiquadFilter();
    airFilter.type = "lowpass";
    airFilter.frequency.value = 280;
    airFilter.Q.value = 0.4;

    airSource.connect(airFilter);
    connectBus(airFilter, cfg.AIR_LEVEL);
    airSource.start();
    trackLoop(airSource);

    reactGain = context.createGain();
    reactGain.gain.value = 1;
    reactGain.connect(master);
  };

  const playBurst = (
    duration: number,
    gain: number,
    filterHz: number,
    filterQ: number,
    attack = 0.04,
  ): void => {
    const context = ensure();
    const bus = reactGain;
    if (!context || !bus) return;

    const buffer = makeNoiseBuffer(context, duration);
    const source = context.createBufferSource();
    source.buffer = buffer;

    const filter = context.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = filterHz;
    filter.Q.value = filterQ;

    const env = context.createGain();
    const now = context.currentTime;
    env.gain.setValueAtTime(0.0001, now);
    env.gain.exponentialRampToValueAtTime(gain, now + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    source.connect(filter);
    filter.connect(env);
    env.connect(bus);

    source.start(now);
    source.stop(now + duration + 0.05);
  };

  const playCheer = (): void => {
    const context = ensure();
    if (!context || !reactGain) return;

    const now = context.currentTime;
    const dur = cfg.CHEER_DURATION;

    // Oleada grave de celebracion.
    playBurst(dur * 0.85, cfg.CHEER_PEAK, 680, 0.5, 0.12);
    playBurst(dur * 0.7, cfg.CHEER_PEAK * 0.65, 1050, 0.42, 0.08);
    playBurst(dur * 0.55, cfg.CHEER_PEAK * 0.4, 420, 0.35, 0.15);

    // Tono ascendente tipo "gol".
    const osc = context.createOscillator();
    const env = context.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(180, now);
    osc.frequency.exponentialRampToValueAtTime(420, now + dur * 0.35);
    osc.frequency.exponentialRampToValueAtTime(260, now + dur);

    env.gain.setValueAtTime(0.0001, now);
    env.gain.exponentialRampToValueAtTime(cfg.CHEER_TONE, now + 0.1);
    env.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    osc.connect(env);
    env.connect(reactGain);
    osc.start(now);
    osc.stop(now + dur + 0.05);

    cheerBoost = 1;
  };

  const playGasp = (intensity: number): void => {
    const power = Math.min(1, Math.max(0.2, intensity));
    playBurst(0.35 + power * 0.25, 0.08 + power * 0.14, 920, 0.7, 0.02);
    playBurst(0.28 + power * 0.2, 0.05 + power * 0.1, 520, 0.55, 0.015);
    gaspBoost = Math.max(gaspBoost, 0.35 * power);
  };

  const playChatter = (): void => {
    playBurst(0.22 + Math.random() * 0.18, 0.04 + Math.random() * 0.05, 760 + Math.random() * 400, 0.65);
  };

  const playGrassRustle = (speed: number): void => {
    if (speed < 420) return;
    const power = Math.min(1, (speed - 420) / 900);
    playBurst(0.06 + power * 0.05, 0.018 + power * 0.035, 1800 + power * 600, 0.8, 0.008);
  };

  const computeThreat = (ctx: AmbientAudioContext): number => {
    if (ctx.phase !== "playing") return 0;

    const nearRightGoal = ctx.ballX > FIELD.WIDTH * 0.68;
    const nearLeftGoal = ctx.ballX < FIELD.WIDTH * 0.32;
    if (!nearRightGoal && !nearLeftGoal) return 0;

    let threat = 0.25;
    if (ctx.ballSpeed > 380) threat += 0.18;
    if (ctx.ballSpeed > 720) threat += 0.22;
    if (ctx.ballLoft < 20 && ctx.ballSpeed > 520) threat += 0.08;
    return Math.min(1, threat);
  };

  return {
    start(): void {
      if (started) return;
      started = true;
      startLoops();
    },

    update(dt, ctx): void {
      if (!running) return;

      const threat = computeThreat(ctx);
      targetBed = cfg.BASE_LEVEL + threat * (cfg.MAX_LEVEL - cfg.BASE_LEVEL);
      targetBed += cheerBoost * cfg.CHEER_BED_BOOST;
      targetBed += swellBoost * cfg.SWELL_BED_BOOST;
      targetBed += gaspBoost * cfg.GASP_BED_BOOST;

      const smooth = 1 - Math.exp(-dt * 4.5);
      currentBed += (targetBed - currentBed) * smooth;

      if (bedGain) bedGain.gain.value = currentBed;
      if (shimmerGain) {
        shimmerGain.gain.value = cfg.SHIMMER_LEVEL + threat * 0.04 + cheerBoost * 0.05;
      }

      cheerBoost = Math.max(0, cheerBoost - dt / cfg.CHEER_DECAY);
      swellBoost = Math.max(0, swellBoost - dt / cfg.SWELL_DECAY);
      gaspBoost = Math.max(0, gaspBoost - dt / cfg.GASP_DECAY);
      swellCooldown = Math.max(0, swellCooldown - dt);

      chatterTimer -= dt;
      if (chatterTimer <= 0 && ctx.phase === "playing") {
        playChatter();
        chatterTimer = cfg.CHATTER_MIN + Math.random() * (cfg.CHATTER_MAX - cfg.CHATTER_MIN);
      }

      if (ctx.ballLoft < 16 && ctx.ballSpeed > 480) {
        grassCooldown -= dt;
        if (grassCooldown <= 0) {
          playGrassRustle(ctx.ballSpeed);
          grassCooldown = 0.14 + Math.random() * 0.1;
        }
      }
    },

    onGoal(): void {
      playCheer();
    },

    onGasp(intensity = 0.7): void {
      playGasp(intensity);
    },

    onAttackSwell(intensity = 0.6): void {
      const clamped = Math.min(1, intensity);
      swellBoost = Math.max(swellBoost, clamped);
      if (swellCooldown > 0) return;
      swellCooldown = 1.15;
      if (clamped > 0.55) {
        playBurst(0.18, 0.035 + clamped * 0.04, 610, 0.48, 0.03);
      }
    },

    dispose(): void {
      running = false;
      started = false;
      stopAll();
    },
  };
}
